import React, { createContext, useState, ReactNode } from 'react'
import { requestOrUndefined } from '../request'

export interface Language {
  code: string
  name: string
  isStandard: boolean
  nativeName: string
  lexikeetCode?: number
  canTextToSpeech: boolean
  lexikeetOnDemand: boolean
  googleTranslatable: boolean
  threeCharCode: string
}

/**
 * Languages is a static object that contains the list of all languages and helper functions to find and filter them.
 * You can use it directly if you don't need to react to the languages state.
 * If you need to react to the languages state, you should use useLanguages() instead.
 */
export const Languages: {
  languages: Record<string, Language>
  find: (code: string | undefined) => Language | undefined
  getOptions: (filter?: (l: Language) => boolean, native?: boolean) => { value: string; name: string }[]
} = {
  languages: {},
  find: (code: string | undefined) => {
    return code !== undefined ? Languages.languages[code] : undefined
  },
  getOptions: (filter: (l: Language) => boolean = () => true, native: boolean = false) => {
    return Object.entries(Languages.languages)
      .filter(([code, l]) => filter(l))
      .map(([code, l]) => ({
        value: code,
        name: native ? l.nativeName : l.name,
      }))
  },
}

interface LanguagesContext {
  languages: Record<string, Language>
  find: (code: string | undefined) => Language | undefined
  getOptions: (filter?: (l: Language) => boolean, native?: boolean) => { value: string; name: string }[]
}

export const languagesContext = createContext<LanguagesContext>({} as LanguagesContext)

export const LanguagesContextProvider: React.FC<{
  children: ReactNode
}> = ({ children }) => {
  const [languages, setLanguages] = useState<Record<string, Language>>({})

  React.useEffect(() => {
    requestOrUndefined<Record<string, Language> | undefined>('/frontend_api/getLanguages').then((response) => {
      if (response) {
        Languages.languages = response
        setLanguages(response)
      }
    })
  }, [])

  return (
    <languagesContext.Provider value={{ languages: languages, find: Languages.find, getOptions: Languages.getOptions }}>
      {children}
    </languagesContext.Provider>
  )
}

/**
 * Hook to use the languages context, which provides the list of all languages and helper functions to find and filter them.
 * If you do not need to react to the languages state (you do if you are doing something with them on initial page render,
 * because they are not loaded instantly), you can just use the Languages object directly. For example, using Languages
 * directly in a popup is safe, but to show the dropdown on the login page is not save
 */
export const useLanguages = () => React.useContext(languagesContext)
